import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginService } from '@shared-angular/services/login.service';
import { ProjectImportApiResponse } from '@shared-angular/services/project-import.types';
import { ChatbotConfig, Project } from '@shared/index';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  public projects: Project[] = [];
  public selectedProject?: Project;
  public bots: ChatbotConfig[] = [];
  public searchOrgShort?: string = undefined;
  public searchString = '';
  private favoriteProjectIds: Set<string> = new Set<string>();
  private loadedFavoritesForUserId: string | null = null;
  private currentSortField: string = 'created';
  private currentSortOrder: 1 | -1 = -1;
  public creatorNames = new Map<string, string>();

  constructor(private http: HttpClient, private loginService: LoginService) {
    this.favoriteProjectIds = new Set<string>();
  }

  public readSortPreference(): { field: string; order: 1 | -1 } {
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(this.sortStorageKey()) ?? 'null');
      if (saved && typeof saved === 'object' && 'field' in saved && 'order' in saved
        && typeof saved.field === 'string' && ['name', 'created', 'area', 'creator'].includes(saved.field)
        && (saved.order === 1 || saved.order === -1)) {
        return { field: saved.field, order: saved.order };
      }
    } catch {
      // Unavailable storage or invalid data must not prevent opening projects.
    }
    return { field: 'created', order: -1 };
  }

  public saveSortPreference(field: string, order: 1 | -1): void {
    try {
      localStorage.setItem(this.sortStorageKey(), JSON.stringify({ field, order }));
    } catch {
      // Sorting still works when browser storage is disabled.
    }
  }

  private sortStorageKey(): string {
    return `bot-admin-project-sort:${this.getCurrentUserId()}`;
  }

  public projectsForSelectedOrg() {
    if (!this.searchOrgShort) {
      return this.projects;
    }
    return this.projects.filter(project =>
      project._id === '-' ||
      project.orgShort.includes(this.searchOrgShort ?? "")
    );

    /*
    return this.projects.filter(project =>
      project._id !== '-' && (
        //project.description.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase()) ||
        //project.name.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase()) ||
        project.orgShort.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase())
      )
    );
    */
  }

  public filterBots() {
    return this.bots.filter(bot => bot.name.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase()) || bot.creatorName.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase()));

    /*
    return this.bots.filter(bot =>
      bot.name.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase()) ||
      bot.creatorName.toLocaleLowerCase().includes(this.searchString.toLocaleLowerCase())
    );
    */
  }

  public canChange(project: Project) {
    if (project._id == '-') return true;
    return project.editMode == 'creator' && project.creator == this.loginService.loggedInUser?._id || project.editMode == 'everyone';
  }

  public canChangeSelectedProject() {
    const project = this.projects.find(p => p._id == this.selectedProject?._id);
    return (project) ? this.canChange(project) : true;
  }

  public async loadProjects() {
    await this.loadFavoriteProjectIdsFromServer();
    this.projects = await firstValueFrom(this.http.get<Project[]>('/api/project'));
    const sort = this.readSortPreference();
    this.sortProjects(sort.field, sort.order);
  }

  public async getProject(id: string): Promise<Project> {
    return await firstValueFrom(this.http.get<Project>('/api/project/' + id));
  }

  public async createProject(project: Project) {
    const newProject = await firstValueFrom(this.http.post<Project>('/api/project', { name: project.name, description: project.description, editMode: project.editMode, orgShort: project.orgShort }));
    this.projects.push(newProject);
    this.sortProjects();
  }

  public async updateProject(project: Project) {
    const updatedProject = await firstValueFrom(this.http.put<Project>('/api/project/' + project._id, project));
    const projectIndex = this.projects.findIndex(p => p._id == project._id);
    if (this.loginService.loggedInUser?._id === updatedProject.creator || this.loginService.loggedInUser?.orgShorts.includes(updatedProject.orgShort)) {
      this.projects[projectIndex] = updatedProject;
    } else {
      this.projects.splice(projectIndex, 1);
    }
    this.sortProjects();
  }

  public async deleteProject(id: string, trashbots: string = '-') {
    const result = await firstValueFrom(this.http.delete<{ deleted: string }>('/api/project/' + id + '/' + trashbots));
    const projectIndex = this.projects.findIndex(p => p._id == result.deleted);
    this.projects.splice(projectIndex, 1);
    this.favoriteProjectIds.delete(result.deleted);
    await this.persistFavoriteProjectIdsToServer();
  }

  public async loadBots() {
    if (this.selectedProject) {
      this.bots = (await firstValueFrom(this.http.get<ChatbotConfig[]>(`/api/project/bots/${this.selectedProject._id}`))).reverse();
    }
  }

  public async exportProjectZIP(project: Project, includeSecrets: boolean) {
    const zip = await firstValueFrom(this.http.get(`/api/toolbox/project/admin/exportaszip/${project._id}?includeSecrets=${String(includeSecrets)}`, { responseType: 'arraybuffer' }));
    const fileName = project.name + "_" + dayjs().format('YYYY-MM-DD') + ".zip";
    this.saveAs(new Blob([zip], { type: 'application/zip' }), fileName);
  }

  public async importProjectFromZip(formData: FormData): Promise<ProjectImportApiResponse> {
    return await firstValueFrom(this.http.post<ProjectImportApiResponse>('/api/toolbox/project/admin/importaszip', formData));
  }

  private saveAs(blob: Blob, filename: string) {
    const fileURL = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.setAttribute('href', fileURL);
    element.setAttribute('download', filename);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  public isFavoriteProject(projectId: string): boolean {
    this.ensureFavoritesUserBinding();
    return this.favoriteProjectIds.has(projectId);
  }

  public async toggleFavoriteProject(projectId: string): Promise<void> {
    this.ensureFavoritesUserBinding();
    const wasFavorite = this.favoriteProjectIds.has(projectId);
    if (wasFavorite) {
      this.favoriteProjectIds.delete(projectId);
    } else {
      this.favoriteProjectIds.add(projectId);
    }
    this.sortProjects(this.currentSortField, this.currentSortOrder);

    try {
      await this.persistFavoriteProjectIdsToServer();
    } catch {
      if (wasFavorite) {
        this.favoriteProjectIds.add(projectId);
      } else {
        this.favoriteProjectIds.delete(projectId);
      }
      this.sortProjects(this.currentSortField, this.currentSortOrder);
    }
  }

  public sortProjects(sortField: string = this.currentSortField, sortOrder: 1 | -1 = this.currentSortOrder) {
    this.currentSortField = sortField;
    this.currentSortOrder = sortOrder;

    this.projects = this.projects.sort((a, b) => {
      const aFavorite = this.isFavoriteProject(a._id);
      const bFavorite = this.isFavoriteProject(b._id);

      if (aFavorite !== bFavorite) {
        return aFavorite ? -1 : 1;
      }

      if (a._id === "-") return -1;
      if (b._id === "-") return 1;

      const valueA = this.getSortableValue(a, sortField);
      const valueB = this.getSortableValue(b, sortField);

      if (valueA < valueB) {
        return -1 * sortOrder;
      }
      if (valueA > valueB) {
        return 1 * sortOrder;
      }
      return a.name.localeCompare(b.name);
    });
  }

  private getSortableValue(project: Project, sortField: string): string | number {
    if (sortField === 'creator') {
      return (this.creatorNames.get(project.creator) ?? '').toLocaleLowerCase();
    }
    if (sortField === 'created') {
      return new Date(project.created).getTime();
    }
    if (sortField === 'area') {
      return project.orgShort.toLocaleLowerCase();
    }
    if (sortField === 'name') {
      return project.name.toLocaleLowerCase();
    }

    const genericValue = (project as unknown as Record<string, unknown>)[sortField];
    if (typeof genericValue === 'number') {
      return genericValue;
    }
    if (typeof genericValue === 'string') {
      return genericValue.toLocaleLowerCase();
    }
    return '';
  }

  private getCurrentUserId(): string {
    return this.loginService.loggedInUser?._id ?? 'anonymous';
  }

  private ensureFavoritesUserBinding(): void {
    const currentUserId = this.getCurrentUserId();
    if (this.loadedFavoritesForUserId === currentUserId) {
      return;
    }

    this.loadedFavoritesForUserId = currentUserId;
    this.favoriteProjectIds = new Set<string>();
  }

  private async loadFavoriteProjectIdsFromServer(): Promise<void> {
    this.ensureFavoritesUserBinding();
    const favoriteProjectIds = await firstValueFrom(this.http.get<string[]>('/api/project/favorites'));
    this.favoriteProjectIds = new Set<string>(favoriteProjectIds.filter((entry): entry is string => typeof entry === 'string'));
  }

  private async persistFavoriteProjectIdsToServer(): Promise<void> {
    this.ensureFavoritesUserBinding();
    await firstValueFrom(this.http.put<{ favoriteProjectIds: string[] }>('/api/project/favorites', {
      favoriteProjectIds: Array.from(this.favoriteProjectIds)
    }));
  }
}
